import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfoPopup from "../CommonComponents/InfoPopup/InfoPopup";
import BubbleSvgButton from "../CommonComponents/BubbleSvgButton/BubbleSvgButton";
import CustomSwitch from "../CommonComponents/CustomSwitch/CustomSwitch.jsx";
import useApi from "../../../helpers/apiHelper/requestHelper";
import { toastify } from "../../../helpers/toast/toastify";
import "./Business.css";
import dropdownAngle from "../../../assets/images/dropdownAngle.svg";
import { setRefetchSpecificUser } from "../../../redux/multicommunity/multicommunity.js";

const monthlyPackages = [
  {
    label: `SMALL $59 / month (max. 1,000 users)`,
    value: {
      title: "$59 - SMALL",
      description: (
        <>
          <p>Max. 1,000 users* / month</p>
          <p>- iOS + Android</p>
        </>
      ),
    },
  },
  {
    label: "MEDIUM $89 / month (max. 5,000 users)",
    value: {
      title: "$89 - MEDIUM",
      description: (
        <>
          <p>Max. 5,000 users* / month</p>
          <p>- iOS + Android</p>
          <p>- Push notifications</p>
        </>
      ),
    },
  },
  {
    label: "LARGE $149 / month (max. 50,000 users)",
    value: {
      title: "$149 - LARGE",
      description: (
        <>
          <p>Max. 50,000 users* / month</p>
          <p>Everything in ‘medium package’ plus:</p>
          <p>- Publish app to Amazon + Huawei app stores</p>
          <p>- App Review Reminders to users</p>
        </>
      ),
    },
  },
  {
    label: "XL $329 / month (unlimited users)",
    value: {
      title: "$329 - XL",
      description: (
        <>
          <p>Unlimited users / month</p>
          <p>Everything in ‘large package’ plus:</p>
          <p>- Priority Support</p>
        </>
      ),
    },
  },
];

const yearlyPackages = [
  {
    label: "SMALL $590 / year (max. 1,000 users)",
    value: {
      title: "SMALL $590 / year (max. 1,000 users)",
      description: (
        <>
          <p>Max. 1,000 users* / month</p>
          <p>- iOS + Android</p>
        </>
      ),
    },
  },
  {
    label: "MEDIUM $890 / year (max. 5,000 users)",
    value: {
      title: "MEDIUM $890 / year (max. 5,000 users)",
      description: (
        <>
          <p>Max. 5,000 users* / month</p>
          <p>- iOS + Android</p>
          <p>- Push notifications</p>
        </>
      ),
    },
  },
  {
    label: "LARGE $1490 / year (max. 50,000 users)",
    value: {
      title: "LARGE $1490 / year (max. 50,000 users)",
      description: (
        <>
          <p>Max. 50,000 users* / month</p>
          <p>Everything in ‘medium package’ plus:</p>
          <p>- Publish app to Amazon + Huawei app stores</p>
        </>
      ),
    },
  },
  {
    label: "XL $3290 / year (unlimited users)",
    value: {
      title: "XL $3290 / year (unlimited users)",
      description: (
        <>
          <p>Unlimited users / month</p>
          <p>Everything in ‘large package’ plus:</p>
          <p>- Priority Support</p>
        </>
      ),
    },
  },
];

const GetYourOwnApp = () => {
  const [isdetail, setIsdetail] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Select App Package");
  const [activeButton, setActiveButton] = useState("Monthly");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user);
  const api = useApi();

  const filterDropdownRef = useRef(null);
  const filterButtonRef = useRef(null);

  // Same logic as FloatingDropdownWithArrow — flips upward when not enough space below
  const calcPosition = () => {
    if (!filterButtonRef.current) return;
    const rect = filterButtonRef.current.getBoundingClientRect();
    // switcher row (~44px) + 4 packages (~44px each) + padding (~44px)
    const estimatedHeight = 270;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < estimatedHeight;
    setDropdownPosition({
      top: openUpward ? rect.top - estimatedHeight - 5 : rect.bottom + 5,
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (isOpen) calcPosition();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", calcPosition, true);
    window.addEventListener("resize", calcPosition);
    return () => {
      window.removeEventListener("scroll", calcPosition, true);
      window.removeEventListener("resize", calcPosition);
    };
  }, [isOpen]);

  const currentPackages =
    activeButton === "Monthly" ? monthlyPackages : yearlyPackages;

  const hasExistingOrder = user?.appOrder?.order === true;

  const existingOrderTitle = user?.appOrder?.title?.label;
  const existingOrderDuration = user?.appOrder?.duration;

  const extractDescriptionText = (descriptionJSX) => {
    if (!descriptionJSX?.props?.children) return [];

    return descriptionJSX.props.children.map((element) => {
      if (element?.props?.children) {
        return element.props.children;
      }
      return "";
    });
  };

  const handleOrderSubmission = async () => {
    if (!selectedPackage) {
      toastify("Please select a package first.");
      return;
    }

    if (!user?.mainLeader?.email) {
      toastify("User email not found.");
      return;
    }

    try {
      const descriptionArray = extractDescriptionText(
        selectedPackage.value.description
      );

      const orderData = {
        title: selectedPackage,
        description: descriptionArray,
        user: user.mainLeader.email,
        duration: activeButton.toLowerCase(),
      };

      const payload = {
        orderData,
        appOrder: true,
      };

      const response = await api("post", "users/sendAppPackage", payload);

      if (response?.status) {
        dispatch(setRefetchSpecificUser(true));
        toastify(response?.message, "success");
        setSelectedOption("Select App Package");
        setSelectedPackage(null);
      } else {
        toastify(response?.message || "Failed to place order.");
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toastify("An error occurred while placing the order.");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (hasExistingOrder && existingOrderTitle && existingOrderDuration) {
      const orderDuration =
        existingOrderDuration.charAt(0).toUpperCase() +
        existingOrderDuration.slice(1);
      setActiveButton(orderDuration);

      setSelectedOption(existingOrderTitle);

      const allPackages = [...monthlyPackages, ...yearlyPackages];
      const existingPackage = allPackages.find(
        (pkg) => pkg.label === existingOrderTitle
      );
      if (existingPackage) {
        setSelectedPackage(existingPackage);
      }
    }
  }, [
    user?.appOrder,
    hasExistingOrder,
    existingOrderTitle,
    existingOrderDuration,
  ]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <div className="accountPerfomance getYourOwnApp mb-sm-0 mb-5">
        <p className="title mb-0 text-lg-center text-start mb-2">
          <span className="pe-2 d-lg-inline d-none text-lg-center">
            Get your own app
          </span>
          <span className="pe-2 d-lg-none d-inline">Get your own app</span>
          <span className="infoSpan">
            <InfoPopup
              contentItems={[
                { text: "Launch Your Own Branded App", type: "heading" },
                {
                  text: "Get a fully branded app — with your name, logo, and colors — live in the iOS and Android app stores in just one month. Pricing starts at $59. Simply choose your estimated monthly user tier to get started.",
                  type: "paragraph",
                },
              ]}
            />
          </span>
        </p>
        <div className="SwithTitle mt-3 d-lg-none d-block">
          <CustomSwitch
            checked={isdetail}
            onChange={() => {
              setIsdetail(!isdetail);
            }}
          />
          <span className="switchText ps-3">See details</span>
        </div>
        {(!isMobile || isdetail) && (
          <>
            <div className="customAppBox w-100 mt-3">
              <div className="dropdownWrapper mb-3">
                <div className="fixedWidth1440">
                  <div className="memberFilteDropdown mb-40px">
                    <div className="dropdown" ref={filterDropdownRef}>
                      <button
                        className="dropdown-btn"
                        onClick={
                          hasExistingOrder ? undefined : () => setIsOpen(!isOpen)
                        }
                        ref={filterButtonRef}
                        disabled={hasExistingOrder}
                        style={{
                          backgroundColor: "",
                          borderColor: "",
                          color: isOpen ? "#000000" : "#9a9b9b",
                        }}
                      >
                        {selectedOption}{" "}
                        <img
                          alt=""
                          className={`arrow ${isOpen && "rotate"}`}
                          src={dropdownAngle}
                        />
                      </button>
                      {isOpen && !hasExistingOrder && (
                        <>
                          <ul
                            className="dropdown-menu"
                            style={{
                              position: "fixed",
                              top: dropdownPosition.top,
                              left: dropdownPosition.left,
                              width: dropdownPosition.width,
                              zIndex: 1050,
                            }}
                          >
                            <div className="switchSide p-0 justify-content-sm-end justify-content-center">
                              <div className="switchBase">
                                <button
                                  className={`${
                                    activeButton === "Monthly" && "active"
                                  }`}
                                  onClick={() => {
                                    setActiveButton("Monthly");
                                    setSelectedOption("Select App Package");
                                    setSelectedPackage(null);
                                  }}
                                >
                                  Monthly
                                </button>
                                <button
                                  className={`${
                                    activeButton === "Yearly" && "active"
                                  }`}
                                  onClick={() => {
                                    setActiveButton("Yearly");
                                    setSelectedOption("Select App Package");
                                    setSelectedPackage(null);
                                  }}
                                >
                                  Yearly
                                </button>
                              </div>
                            </div>
                            {currentPackages.map((packageItem, index) => (
                              <li key={index}>
                                <button
                                  onClick={() => {
                                    setSelectedOption(packageItem.label);
                                    setSelectedPackage(packageItem);
                                    setIsOpen(false);
                                  }}
                                >
                                  {packageItem.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-2 buttonWrapper">
                <BubbleSvgButton
                  onClick={handleOrderSubmission}
                  text={hasExistingOrder ? "Already Ordered App" : "Order App"}
                  type="button"
                  variant="black"
                  disabled={hasExistingOrder}
                  mainDivStyle={{
                    zIndex: "0",
                  }}
                  containerStyling={{
                    justifyContent: "center",
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default GetYourOwnApp;
